import { ProjectDemoData } from './types';
import { extractNthItem } from './director-engine';

// Project A：保险项目
// 数据来源与口径：
// 1. 产品/内容/合规知识：项目信息/保险项目.md + 保险项目-素材分析报告.md（三轮头部视频逐条拆解，样本量已脱敏）
// 2. 投放数据：保险项目-1.csv（2026-07真实明细锚点月，金额×0.1脱敏、账户匿名化）；
//    2026-01~06及09为按锚点月节奏推演的演示数据，所有 CPA=消耗/转化数 严格自洽。
export const projectA: ProjectDemoData = {
  id: 'jiankangfu',
  name: '保险项目',
  category: '保险',
  initType: 'HISTORICAL_DATA',
  currentStage: '跑量优化期',
  businessObjective: '稳定跑量成本，放大慢病可投与子女责任主线',
  targetMedia: '头条信息流（巨量引擎）',

  // AI拆解页「分析数据概览」（DEMO 展示数据，口径与投放数据脱敏规则一致）
  analysisStats: {
    hitMaterialCount: 568,
    analyzedMaterialCount: 8653,
    dataStartDate: '2026-01-01',
    dataEndDate: '2026-09-15',
    lastUpdatedAt: '2026-09-15 09:30',
  },

  productUnderstanding: {
    targetAudience: '25-45岁子女，给父母购买重疾保障',
    coreSellingPoints: [
      '最高70岁也有机会投保',
      '部分三高/糖尿病/结节可投',
      '责任内最高100万保障',
      '包含先进治疗、康复护理责任',
      '每月9.65元起'
    ],
    mainScenarios: [
      '子女给父母补充保障',
      '医院看病医保缺口',
      '慢病/结节可投投保',
      '异地子女牵挂父母健康'
    ],
    decisionPath: [
      '观看者：80/90后子女刷到广告（父母/70岁/三高结节钩子先点名）',
      '搜索者：子女搜索「父母有高血压还能买重疾险吗」，用疾病清单和评论问答承接',
      '决策人：子女主导，与父母商量（父母常为反对者：觉得贵、不想体检）',
      '付款人：25-45岁子女承担每月保费',
      '被保人：父母本人（18-70岁符合投保条件人群）'
    ],
    currentStrategy: '原生标题情感向+明星二剪双主力放量；慢病可投清单为第一钩子，100万责任拆分承接高意向流量'
  },

  learningWorkflow: {
    brief: {
      businessObjective: '稳定跑量成本，放大慢病可投与子女责任主线',
      currentStage: '跑量优化期',
      targetMedia: '头条信息流（巨量引擎）',
      targetAudience: '25-45岁子女，父母50-70岁，有三高/结节/糖尿病，担心父母大病保障',
      keySellingPoints: [
        '最高70岁符合条件可投',
        '部分三高/糖尿病无并发症可投，三大结节可投',
        '责任内最高100万保障，含住院治疗、先进治疗、康复护理',
        '每月9.65元起，具体以页面测算为准'
      ],
      forbiddenDirections: '不能承诺都能买，不能说确诊就赔100万，不能使用绝对化用语',
      currentProblems: '需要找到跑量突破口，测试核心转化钩子',
      costTarget: 'CPA 150以内',
      volumeTarget: '日耗20万',
      specialRequirements: '重点突出慢病可投和子女责任'
    },
    materialLearning: {
      videoCount: 36,
      sections: [
        {
          title: '主要人群（按事件状态分层）',
          items: [
            'S级：父母有三高/结节/糖尿病，但尚未发生严重重疾的子女',
            'S级：刚陪父母体检/看病/住院/处理医保结算的子女',
            'S级：异地工作、不能长期陪伴父母的80/90后子女',
            'A级：父母50-70岁、健康状况开始变化的普通家庭',
            'A级：女性子女/家庭健康管理者',
            'A级：下沉城市/县城/农村普通家庭'
          ]
        },
        {
          title: '高频场景',
          items: [
            'S级：医院门诊/急诊/住院/结算（风险已发生的证据环境）',
            'S级：疾病/慢病清单首帧（意图筛选器，不是完整内容）',
            'A级：家庭客厅/餐桌/厨房（谁在替谁做决定）',
            'A级：高铁站/机场/车内/通勤（异地子女符号）',
            'A级：普通父母真实生活（餐馆/雪地/小区，降低广告感）',
            'B级：棚拍主播/专家（前贴组件，注意同质化）'
          ]
        },
        {
          title: '内容切入方式（钩子机制）',
          items: [
            '健康状态钩子：爸妈有高血压，还能不能买重疾险？',
            '年龄窗口钩子：80/90后，爸妈没超过70岁的先看一下',
            '事件型钩子：陪爸妈住一次院，才知道有医保也不是万事大吉',
            '子女关系钩子：小时候爸妈撑起我们，现在轮到我们提前准备',
            '产品认知钩子：这个100万，不是一个简单确诊金',
            '人群清单钩子：以下三类人，建议先帮父母了解重疾保障',
            '反常识钩子：先别急着给爸妈买重疾险（3-5秒内必须反转）'
          ]
        },
        {
          title: '剧情规律',
          items: [
            '先回答「爸妈能不能买」，再回答「保多少」',
            '家庭关系建立动机 → 医院/风险建立可信度 → 产品责任解决问题',
            '情绪四种：风险恐惧/子女愧疚/父母负担感/提前准备的掌控感',
            '情绪从负面进入，最终落在「可行动」的掌控感，不能一直吓人'
          ]
        },
        {
          title: '文案规律',
          items: [
            '0-3秒先筛人：年龄/关系/健康状态/事件/情绪，越具体越降低无关感',
            '产品进入一般在8-15秒（冷流量），高意向流量可前置到0-3秒',
            '15-35秒只讲1-2个最相关责任，不堆卖点',
            '35秒后条件说明+CTA，引导点击测算'
          ]
        },
        {
          title: '产品承接方式',
          items: [
            '高频承接：18-70岁符合条件 + 慢病/结节可投条件 + 125种重疾',
            '责任拆分：住院治疗10万 + 先进治疗50万 + 康复护理40万',
            '住院治疗满足条件每月给付5000、最多10个月',
            '价格放产品理解之后：每月9.65元起，以页面测算为准'
          ]
        },
        {
          title: '信任机制',
          items: [
            '医院证据感：门诊大厅/急诊/病房/结算区/自助机',
            '普通人证言感：老人本人/女儿/儿子/夫妻/家庭',
            '产品证据感：手机投保页/责任卡/产品截图',
            '问题证据感：评论区问题/疾病清单/体检慢病问题',
            '稳定素材通常两种信任组合，例如疾病清单+真人解释'
          ]
        },
        {
          title: '视觉风格',
          items: [
            '节目冷开+达人后贴成熟但同质化明显，只作产能模型',
            '真实场景与普通父母出镜降低商业片感',
            '复杂责任用字幕卡/责任卡承担，不让老人背条款'
          ]
        }
      ],
      recommendations: [
        '跑量主线不是「中老年本人买保险」，而是「子女给爸妈补保障」',
        '慢病/结节可投是最强转化钩子，但必须绑定健康告知条件',
        '医院场景是信任组件，家庭场景是情绪组件，两者组合投放'
      ]
    },
    strategySummary: [
      '子女是核心观看者和付款决策者：内容先点名「80/90后」「给爸妈」，再讲产品',
      '慢病/结节可投是第一转化阻力也是最强入口，必须绑定健告条件表达',
      '100万必须拆成住院治疗10万/先进治疗50万/康复护理40万，不做单一确诊金表达',
      '当期数据：原生标题+明星二剪形式占消耗97%，双主力继续放量',
      'AI素材账户消耗占比51.9%、成本与常规账户基本持平，可继续放量观察'
    ]
  },

  optimization: {
    overview: {
      periodLabel: '2026-01-01 ~ 2026-09-10',
      platform: '头条信息流（巨量引擎）',
      conversionLabel: '转化成本',
      accountCount: 18,
      totalSpend: 2247024.83,
      conversions: 1406,
      costPerConversion: 1598.17,
      ctrPct: 0.81,
      cvrPct: 0.76
    },
    monthly: [
      { month: '2026-01', spend: 95604.53, conversions: 46, costPerConversion: 2078.36, ctrPct: 0.55, cvrPct: 0.52, materialCount: 45, accountCount: 4 },
      { month: '2026-02', spend: 140074.05, conversions: 74, costPerConversion: 1892.89, ctrPct: 0.61, cvrPct: 0.58, materialCount: 62, accountCount: 6 },
      { month: '2026-03', spend: 176702.69, conversions: 100, costPerConversion: 1767.03, ctrPct: 0.66, cvrPct: 0.63, materialCount: 88, accountCount: 8 },
      { month: '2026-04', spend: 240949.34, conversions: 143, costPerConversion: 1684.96, ctrPct: 0.72, cvrPct: 0.68, materialCount: 118, accountCount: 11 },
      { month: '2026-05', spend: 305859.64, conversions: 190, costPerConversion: 1609.79, ctrPct: 0.78, cvrPct: 0.72, materialCount: 142, accountCount: 13 },
      { month: '2026-06', spend: 348641.19, conversions: 225, costPerConversion: 1549.52, ctrPct: 0.84, cvrPct: 0.79, materialCount: 164, accountCount: 16 },
      { month: '2026-07', spend: 412475.16, conversions: 273, costPerConversion: 1510.9, ctrPct: 0.9, cvrPct: 0.85, materialCount: 186, accountCount: 18 },
      { month: '2026-08', spend: 386486.38, conversions: 262, costPerConversion: 1475.14, ctrPct: 0.93, cvrPct: 0.88, materialCount: 198, accountCount: 17 },
      { month: '2026-09(1-10日)', spend: 140231.85, conversions: 93, costPerConversion: 1507.87, ctrPct: 0.91, cvrPct: 0.87, materialCount: 86, accountCount: 17 }
    ],
    tagStats: [
      { label: '头条首发素材', materialCount: 43, spend: 168050.33, spendSharePct: 40.7, costPerConversion: 1507.85, note: '平台判定为首发创作的素材' },
      { label: '头条优质素材', materialCount: 27, spend: 85978.72, spendSharePct: 20.8, costPerConversion: 1564.67, note: '与首发素材存在重叠（8条双标签）' },
      { label: '未打标签', materialCount: 124, spend: 162094.48, spendSharePct: 38.4, costPerConversion: 1487.11 },
      { label: '搬运打压素材', materialCount: 1, spend: 145.72, spendSharePct: 0.0, costPerConversion: null, note: '平台判定搬运，已停止投放' }
    ],
    dimensions: [
      { group: '素材形式', name: '原生标题（自然流）', materialCount: 66, spend: 215320.62, spendSharePct: 52.2, costPerConversion: 1516.34 },
      { group: '素材形式', name: '明星二剪（授权）', materialCount: 80, spend: 184671.89, spendSharePct: 44.8, costPerConversion: 1501.4 },
      { group: '素材形式', name: '达人二剪（非明星）', materialCount: 10, spend: 12482.65, spendSharePct: 3.0, costPerConversion: 1783.24, note: '成本显著高于整体' },
      { group: '账户类型', name: 'AI素材账户', spend: 214218.36, spendSharePct: 51.9, costPerConversion: 1530.13 },
      { group: '账户类型', name: '常规账户', spend: 192737.21, spendSharePct: 48.1, costPerConversion: 1494.09 }
    ],
    topMaterials: [
      { name: '多数人忽略的家庭隐患，父母无退休金家庭必看#爸妈养生 #保险项目', spend: 101614.76, conversions: 68, costPerConversion: 1494.33, ctrPct: 0.55, cvrPct: 0.82, tags: ['头条首发素材'] },
      { name: '直出口播·责任讲解（自制）', spend: 36852.42, conversions: 28, costPerConversion: 1316.16, ctrPct: 5.43, cvrPct: 2.19, tags: ['头条优质素材'] },
      { name: '达人二剪·剧情向（授权二创）', spend: 18433.53, conversions: 12, costPerConversion: 1536.13, ctrPct: 1.08, cvrPct: 0.34, tags: ['未打标签'] },
      { name: '低谷，其实是老天赠予我们的礼物 #陪护 #保险项目', spend: 16119.59, conversions: 13, costPerConversion: 1239.97, ctrPct: 0.55, cvrPct: 1.48, tags: ['头条首发素材', '头条优质素材'] },
      { name: '只要中了这3个坑 小心一分不赔！#生活 #科普 #健康', spend: 9033.57, conversions: 3, costPerConversion: 3011.19, ctrPct: 3.36, cvrPct: 0.61, tags: ['头条首发素材'] },
      { name: '明星二剪·授权素材（二剪）', spend: 7996.88, conversions: 8, costPerConversion: 999.61, ctrPct: 0.9, cvrPct: 1.91, tags: ['未打标签'] }
    ],
    bestCpaMaterials: [
      { name: '明星二剪·授权素材（二剪）', spend: 7996.88, conversions: 8, costPerConversion: 999.61, ctrPct: 0.9, cvrPct: 1.91 },
      { name: '低谷，其实是老天赠予我们的礼物 #陪护 #保险项目', spend: 16119.59, conversions: 13, costPerConversion: 1239.97, ctrPct: 0.55, cvrPct: 1.48 },
      { name: '曾以为幸福很简单，平安健康才是一个家最大的福气 #保险项目', spend: 7845.46, conversions: 6, costPerConversion: 1307.58, ctrPct: 0.82, cvrPct: 0.83 },
      { name: '直出口播·责任讲解（自制）', spend: 36852.42, conversions: 28, costPerConversion: 1316.16, ctrPct: 5.43, cvrPct: 2.19 },
      { name: '人世间最动人的亲情，是我全力以赴护你余生安稳 #纪录片 #父女情深', spend: 6830.36, conversions: 5, costPerConversion: 1366.07, ctrPct: 0.33, cvrPct: 1.63 },
      { name: '56岁出走山河的决心，我终于为自己活一次 #母女 #保险项目', spend: 5485.9, conversions: 4, costPerConversion: 1371.47, ctrPct: 0.9, cvrPct: 1.18 }
    ],
    effectiveDirections: [
      '原生标题情感/陪护向：成本低至1239~1372元（低谷陪护、曾以为幸福、56岁出走），跑量与成本兼得',
      '直出口播（自制）：点击率5.43%为全量最高，CPA 1316.16优于整体17.6%',
      '明星二剪授权素材：CPA 999.61为当期最优，转化率1.91%',
      '慢病可投清单钩子：36条头部素材确认的最强转化点，继续作为内容资源第一优先'
    ],
    weakDirections: [
      '达人二剪（非明星）：消耗占比仅3.0%，CPA 1783.24比整体高11.6%，暂停普通二剪',
      '恐吓/坑点科普向（如「只要中了这3个坑」）：CPA 3011.19，约为整体2倍，停止该方向',
      '搬运打压素材：平台判定搬运，消耗已收缩至145.72元，立即停止'
    ],
    nextRoundPlan: [
      '内容资源分配：慢病/结节可投清单25% + 子女保障/年龄窗口20% + 医院医保缺口20% + 责任拆分15% + 先进治疗康复护理10% + 家庭短剧与父母本人5% + 创新形式5%',
      '形式配比：原生标题情感向 + 明星二剪双主力放量；直出口播承接高意向搜索流量',
      'AI素材账户维持51.9%投放占比，重点考核AI生成素材的前3秒质量与过审率',
      '单变量测试：同一脚本只换前3秒钩子（慢病可投 vs 子女责任 vs 医院费用），观察3秒率与5秒率'
    ],
    dataSourceNote: '数据来源：头条投放明细导出（2026-07为真实明细锚点月）；2026-01~06及09为演示推演。金额与数量已脱敏（消耗/转化数/素材数/账户数均按比例缩放），比率指标保持真实趋势。标签/形式/素材明细为2026-07当期口径。'
  },

  productSkill: {
    facts: [
      {
        category: '产品定位',
        key: 'product_type',
        value: '保险项目，给父母补充重疾保障'
      },
      {
        category: '投保年龄',
        key: 'age_range',
        value: '18-70岁符合投保条件人群'
      },
      {
        category: '月保费',
        key: 'monthly_premium',
        value: '每月9.65元起，具体以年龄、健康情况和页面测算为准'
      },
      {
        category: '总保额',
        key: 'total_coverage',
        value: '责任内总保额100万，分不同责任给付'
      },
      {
        category: '保障疾病',
        key: 'covered_diseases',
        value: '保障125种重大疾病，具体疾病名称及标准详见条款'
      },
      {
        category: '重疾及住院治疗保险金',
        key: 'treatment_coverage',
        value: '10万，承接首次确诊/住院治疗压力'
      },
      {
        category: '重疾先进治疗保险金',
        key: 'advanced_treatment',
        value: '50万，承接CAR-T、质子重离子等先进治疗'
      },
      {
        category: '重疾康复护理保险金',
        key: 'rehab_coverage',
        value: '40万，承接出院后康复、护理、养病费用'
      },
      {
        category: '住院相关给付',
        key: 'hospital_payment',
        value: '住院治疗满足条件每月赔5000，最多10个月'
      },
      {
        category: '等待期',
        key: 'waiting_period',
        value: '90天，意外伤害无等待期'
      },
      {
        category: '免赔额',
        key: 'deductible',
        value: '重疾险不设免赔额'
      },
      {
        category: '健康告知',
        key: 'underwriting',
        value: '三大结节可投；高血压、糖尿病无并发症可投；满足健康告知其他要求'
      }
    ],
    compliance: [
      {
        ruleType: 'FORBIDDEN',
        content: '不能说"70岁以内都能买"，必须说明"符合条件可投"',
        severity: 'HIGH'
      },
      {
        ruleType: 'FORBIDDEN',
        content: '不能说"确诊就赔100万"，必须说明是责任分拆',
        severity: 'HIGH'
      },
      {
        ruleType: 'FORBIDDEN',
        content: '不能说"有三高都能买/带病都能买"，必须说明条件',
        severity: 'HIGH'
      },
      {
        ruleType: 'FORBIDDEN',
        content: '不能用"都、全、100%覆盖"等绝对化用语',
        severity: 'HIGH'
      },
      {
        ruleType: 'REQUIRED',
        content: '每月9.65元必须补充"具体以页面测算为准"',
        severity: 'HIGH'
      },
      {
        ruleType: 'REQUIRED',
        content: '慢病可投必须补充"符合健康告知条件"',
        severity: 'HIGH'
      }
    ],
    audienceSegments: [
      {
        segmentName: 'S级 - 父母有慢病子女',
        description: '25-45岁，父母有三高/结节/糖尿病',
        lifeStage: '子女孝敬父母',
        coreNeed: '给父母找一款能买的重疾险',
        painPoint: '父母体检有问题，很多产品买不了，担心父母出事没钱治',
        trigger: '父母体检报告出来，医生提醒慢病管理',
        concern: '父母这种身体情况到底还能不能投保'
      },
      {
        segmentName: 'S级 - 异地工作子女',
        description: '80/90后在外地工作，父母留在家乡',
        lifeStage: '异地打拼子女',
        coreNeed: '不能陪伴，但至少给父母留份保障',
        painPoint: '不在父母身边，最怕突然生病钱和人都跟不上，出事还是子女扛',
        trigger: '春节离家、高铁站、加班夜晚',
        concern: '自己不在身边，父母真出事怎么办'
      },
      {
        segmentName: 'S级 - 陪父母看病子女',
        description: '已经陪父母看病/体检/住院，亲身感受医院费用',
        lifeStage: '子女照顾父母',
        coreNeed: '提前准备好保障，真出事不慌乱',
        painPoint: '亲眼看到医保报销后自费压力还很大，知道需要补充',
        trigger: '刚陪父母看完病、住院结算后',
        concern: '医保不够用，未来大病费用怎么办'
      },
      {
        segmentName: 'A级 - 女性健康管理者',
        description: '女性子女，通常是家庭健康管理者，负责父母体检、买药、保障安排',
        lifeStage: '家庭健康负责人',
        coreNeed: '提前为父母做好健康保障规划',
        painPoint: '担心父母年纪大了，万一出事没钱治，拖累家庭',
        trigger: '家庭聚会、体检安排',
        concern: '怎么选到合适父母的产品'
      }
    ],
    needsAndPainPoints: [
      {
        audienceSegment: 'S级 - 父母有慢病子女',
        need: '找到一款父母符合条件能投保的重疾险',
        painPoint: '父母年纪大还有慢病，很多重疾险都买不了',
        trigger: '体检报告出来，看到结节/高血压/糖尿病，担心以后出事',
        concern: '这种身体情况到底还能不能买到重疾险'
      },
      {
        audienceSegment: 'S级 - 异地工作子女',
        need: '虽然不能陪伴在身边，但至少给父母一份保障',
        painPoint: '不在父母身边，真出事了赶回去都来不及，提前准备安心',
        trigger: '过节回家离开父母，在高铁站看到父母送行',
        concern: '自己不能天天照顾，真出事怎么办'
      },
      {
        audienceSegment: 'S级 - 陪父母看病子女',
        need: '补充医保缺口，让父母治病不用太心疼钱',
        painPoint: '亲眼看到医保报销后，自己还要付很多钱，大病压力还是很大',
        trigger: '陪父母看完病，看到结算单才知道缺口多大',
        concern: '万一下次再大病，家庭现金流够不够用'
      },
      {
        audienceSegment: 'A级 - 女性健康管理者',
        need: '提前为父母规划好重疾保障，尽到子女责任',
        painPoint: '作为女儿，更关心父母健康，担心父母有事没钱治',
        trigger: '每年体检安排，看到很多同龄人都在给父母买',
        concern: '怎么选到适合父母身体状况的产品'
      }
    ],
    scenarios: [
      {
        name: '医院门诊大厅',
        description: '陪父母检查完在门诊大厅',
        userContext: '刚陪父母看完病，感慨医保不够用',
        productEntrance: '说到大病还是需要补充重疾险，这款符合条件可以看看'
      },
      {
        name: '家庭餐桌聊天',
        description: '一家人吃饭，父母说不想买保险浪费钱',
        userContext: '子女劝父母了解一下，真出事还是子女扛',
        productEntrance: '子女说：现在每月只要几块钱起，符合条件先了解一下'
      },
      {
        name: '高铁站车内口播',
        description: '异地子女返程路上',
        userContext: '过完节离开家，想到父母年纪大了，放心不下',
        productEntrance: '不能天天陪爸妈，至少先把保障安排上'
      },
      {
        name: '体检报告看结节',
        description: '拿着体检报告看到结节三高',
        userContext: '帮父母看体检报告，担心买不了保险',
        productEntrance: '这款产品部分符合条件也有机会投保，可以先测算'
      }
    ],
    benefitMessages: [
      {
        audienceSegment: 'S级 - 父母有慢病子女',
        benefit: '部分三高、糖尿病、结节人群符合条件也有机会投保',
        messagingAngle: '不要因为父母有慢病就放弃，先看看能不能投',
        productPlacement: '父母有这些毛病，先点击测算看看能不能投'
      },
      {
        audienceSegment: 'S级 - 异地工作子女',
        benefit: '责任内最高100万保障，给父母补充大病现金流',
        messagingAngle: '不能天天陪伴，但至少提前准备，不怕万一',
        productPlacement: '异地打拼的子女，给爸妈看看这份保障'
      },
      {
        audienceSegment: 'S级 - 陪父母看病子女',
        benefit: '含住院治疗、先进治疗、康复护理责任，补上医保缺口',
        messagingAngle: '陪爸妈住一次院才知道，医保不够用重疾险来补',
        productPlacement: '已经陪父母看过病，更知道需要这份保障'
      },
      {
        audienceSegment: 'A级 - 女性健康管理者',
        benefit: '每月9.65元起，责任内最高100万保障，给父母补一层大病保障',
        messagingAngle: '作为女儿，更要为父母提前准备好保障',
        productPlacement: '现在符合条件可以了解，点击测算看看'
      }
    ],
    creativeRecommendations: [
      {
        name: '慢病可投清单口播',
        character: '子女主播',
        characterRelationship: '主播对着镜头和观众聊天',
        scene: '居家口播+疾病清单图',
        hook: '爸妈有高血压、糖尿病、结节，重疾险是不是买不了了？',
        contentFormat: '疾病清单图+口播',
        storyDirection: '告诉观众别急着放弃，这款产品部分符合条件也能投',
        visualStyle: '清单图前贴，清晰直接，快速筛选人群',
        description: '慢病可投是最强转化点，快速命中目标人群'
      },
      {
        name: '异地子女高铁站直讲',
        character: '异地工作子女',
        characterRelationship: '第一人称分享',
        scene: '高铁站/车内',
        hook: '不能天天陪爸妈，至少先把大病保障看一眼',
        contentFormat: '真人自拍直讲',
        storyDirection: '异地子女讲自己的顾虑和准备，引发情绪共鸣',
        visualStyle: '真实场景，第一人称，自然亲切',
        description: '触动异地子女情绪，信任感强，转化好'
      },
      {
        name: '医院结算口播',
        character: '刚陪爸妈看完病的子女',
        characterRelationship: '第一人称分享',
        scene: '医院结算自助机旁',
        hook: '陪爸妈住一次院才知道，有医保也不是万事大吉',
        contentFormat: '医院自拍直讲',
        storyDirection: '讲医保缺口，这款产品补上治疗、先进治疗和康复费用',
        visualStyle: '真实医院场景，冲击力强，信任感高',
        description: '医院场景信任度最高，痛点直观，转化好'
      },
      {
        name: '家庭餐桌劝说情景剧',
        character: '女儿 + 妈妈',
        characterRelationship: '母女',
        scene: '家里客厅餐桌',
        hook: '妈，你总说不用买，真有事不还是孩子扛吗？',
        contentFormat: '家庭短剧',
        storyDirection: '妈妈不想买，女儿劝说，最后自然带出产品',
        visualStyle: '生活化家庭场景，暖色调，真实感强',
        description: '击中真实家庭沟通场景，很多父母不愿买，子女劝说引发共鸣'
      }
    ],
    complianceRules: [
      '不得说"70岁以内都能买"，必须说"最高70岁符合条件可投"',
      '不得说"确诊就赔100万"，必须说"责任内最高100万，按不同责任给付"',
      '不得说"三高都能买"，必须说"部分三高、糖尿病无并发症符合条件可投"',
      '不得使用"都、全、100%"等绝对化用语',
      '每月9.65元起必须补充"具体以年龄、健康情况和页面测算为准"',
      '必须说明90天等待期，意外伤害无等待期',
      '不得承诺投保就能赔，必须说明符合条款条件才可给付'
    ]
  },

  director: {
    optimization: {
      quickPrompts: [
        '最近什么素材表现最好？',
        '哪类素材跑量能力最强？',
        '哪类素材首发率最高？',
        '哪类素材优质率最高？',
        '真人和AI素材表现有什么差异？',
        '最近素材应该重点做什么？',
        '有哪些方向建议减少？'
      ],
      mockResponses: [
        {
          keywords: ['表现最好', '最好'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期投放数据（金额与数量已脱敏）：

【结论】
表现最好要分两个口径看：跑量最大和成本最优不是同一条素材。跑量王是原生标题情感向，成本王是明星二剪。

【数据依据】
- 跑量Top1：「多数人忽略的家庭隐患，父母无退休金家庭必看」，消耗 10.16万，68个转化，转化成本 1494.33，带首发标签
- 成本最优（消耗≥5000）：明星二剪·授权素材，转化成本 999.61，转化率 1.91%，比整体 1598.17 低 37.4%
- 高点击之王：直出口播·责任讲解，点击率 5.43%（全量最高），转化成本 1316.16

【判断】
1. 原生标题「父母/退休金/家庭隐患」类情感向素材既能起量、成本又贴着整体线，是当前的规模盘
2. 明星二剪的转化效率显著更高，但消耗只有 0.8万，还没放够量，属于「高效率低消耗」的潜力款
3. 直出口播点击强、转化中上，适合承接高意向流量

【下一轮建议】
1. 明星二剪按「高转化但低消耗」处理：保留后段转化结构，只重测前3秒和首帧，把量放起来
2. 原生标题情感向再裂变3-5条（陪护/家庭责任/父母视角），守住规模盘
3. 直出口播继续承接搜索和高意向，不承担冷流量首触`;
          }
        },
        {
          keywords: ['跑量'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期投放数据（金额与数量已脱敏）：

【结论】
跑量能力最强的是「原生标题（自然流）+ 明星二剪」双主力，两者合计占消耗 97%。

【数据依据】
- 原生标题（自然流）：66条素材，消耗 21.53万，占 52.2%，转化成本 1516.34
- 明星二剪（授权）：80条素材，消耗 18.47万，占 44.8%，转化成本 1501.40
- 达人二剪（非明星）：10条素材，占仅 3.0%，转化成本 1783.24（全场最差）

【判断】
1. 原生标题类素材的结构（真实家庭/陪护/父母视角 + 产品自然承接）是当前最稳的跑量结构
2. 明星二剪跑量接近一半且成本更低，说明「授权明星素材 + 后段产品承接」的组合既起量又保效率
3. 需要警惕的是双主力同质化：36条历史头部素材里「节目冷开+达人后贴」就因为首帧高度相似被用户识别为模板

【下一轮建议】
1. 双主力维持，但每条新素材首帧必须与在投素材做差异检查
2. 把明星二剪的成本优势放大：从 0.8万提到 2万+ 消耗级再评估
3. 达人二剪暂停，除非拿到新的达人授权`;
          }
        },
        {
          keywords: ['首发'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期投放数据（金额与数量已脱敏）：

【结论】
首发素材是规模主力（消耗占 40.7%），但首发判定本身不等于成本优势——它的转化成本 1507.85 与整体 1598.17 基本持平。

【数据依据】
- 头条首发素材：43条，消耗 16.81万，占 40.7%，转化成本 1507.85
- 对照：未打标签素材 124条，占 38.4%，转化成本 1487.11（反而最低）
- 跑量Top1「多数人忽略的家庭隐患」就是首发素材

【判断】
1. 首发标签解决的是「平台流量判定和素材保护」，它保证素材能起量，不直接降低转化成本
2. 成本主要由内容结构决定：同样是首发，情感向（低谷陪护 1239.97）和恐吓科普向（3个坑 3011.19）差 2.4 倍

【下一轮建议】
1. 保持首发素材供给节奏，把首发当作「起量门票」而不是「成本护身符」
2. 成本优化精力放在内容结构上：先回答「能不能买」，再做责任拆分，最后才谈价格`;
          }
        },
        {
          keywords: ['优质'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期投放数据（金额与数量已脱敏）：

【结论】
优质素材占消耗 20.8%，转化成本 1564.67，略高于整体 1598.17 的均值线附近——「优质」标签更接近平台对素材质量和原创性的判定，不是低成本保证。

【数据依据】
- 头条优质素材：27条，消耗 8.60万，占 20.8%，转化成本 1564.67
- 其中 8 条素材同时带首发+优质双标签
- 对照：未打标签素材转化成本 1487.11，首发素材 1507.85

【判断】
1. 三类标签的成本差距都在 ±5% 以内，说明当前成本差异主要来自内容方向而非标签
2. 优质素材的价值在于生命周期和过审稳定性，这两点数据里看不到但影响长期投放

【下一轮建议】
1. 优质素材保量即可，不作为单独的放量策略
2. 成本优化继续按内容方向走：情感/陪护向优先，恐吓科普向停投`;
          }
        },
        {
          keywords: ['真人', 'AI'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期投放数据（金额与数量已脱敏）：

【结论】
AI素材账户的消耗占比已经达到 51.9%，转化成本 1530.13，只比常规账户（1494.09）高 2.4%——AI素材已经可以和常规素材平起平坐地放量。

【数据依据】
- AI素材账户：消耗 21.42万，占 51.9%，转化成本 1530.13
- 常规账户：消耗 19.27万，占 48.1%，转化成本 1494.09

【判断】
1. AI素材承担了过半消耗，成本差距在 40 元以内，说明AI生成的口播/清单类素材在保险这个品类已经可用
2. 但要区分「AI生成素材可用」和「AI素材随便做都行」：成本仍略高，差距大概率在前3秒的人物质感和口播自然度

【下一轮建议】
1. AI素材账户维持当前占比继续放量
2. AI素材重点考核两个点：前3秒人脸/口型的自然度、疾病清单类画面的合规小字是否完整
3. 每月对比一次 AI vs 常规的转化成本，差距扩大到 10% 以上时再收缩AI占比`;
          }
        },
        {
          keywords: ['单均', '消耗最高'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期投放数据（金额与数量已脱敏），单条素材消耗 Top5：

【数据依据】
1. 「多数人忽略的家庭隐患，父母无退休金家庭必看」— 消耗 10.16万，68转化，成本 1494.33
2. 「直出口播·责任讲解（自制）」— 消耗 3.69万，28转化，成本 1316.16，点击率 5.43%
3. 「达人二剪·剧情向（授权二创）」— 消耗 1.84万，12转化，成本 1536.13
4. 「低谷，其实是老天赠予我们的礼物 #陪护」— 消耗 1.61万，13转化，成本 1239.97
5. 「只要中了这3个坑 小心一分不赔！」— 消耗 0.90万，3转化，成本 3011.19

【判断】
1. Top1 是典型的原生标题情感+恐惧混合钩子（父母/退休金/家庭隐患），体量断层领先
2. 前四名的成本都优于或接近整体（1598.17），唯独第5名「坑点恐吓」成本是整体2倍——高点击（3.36%）没换来转化

【下一轮建议】
1. 对 Top1 做单变量裂变：换演员（女儿/儿子/父母本人）、换场景（医院门口/车内/居家），后段责任拆分不动
2. 「3个坑」类恐吓素材停投（也符合合规原则：不制造过度恐吓）`;
          }
        },
        {
          keywords: ['重点', '下一轮', '分配'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解 和当期数据，下一轮素材重点：

【结论】
内容上围绕「能不能买 → 医保缺口 → 100万怎么拆」三层递进，形式上原生标题+明星二剪双主力继续。

【数据依据】
- 原生标题 52.2% + 明星二剪 44.8%，双主力结构已经验证
- AI素材账户占 51.9%，产能有保障
- 成本最优的三条素材全部是情感/陪护向原生内容（999~1316元）

【判断】
36条头部素材的共识：先解决「爸妈能不能买」（最强入口），医院/家庭场景建立信任，100万责任拆分减少前后端落差。

【下一轮建议】（内容资源分配）
1. 慢病/结节可投清单 + 评论问答：25%（高意向转化）
2. 子女给父母保障/年龄窗口：20%（核心购买链路）
3. 医院/住院/医保缺口：20%（信任和风险具象化）
4. 责任拆分/100万怎么保：15%（中高意向教育）
5. 先进治疗/康复护理：10%（差异化）
6. 家庭短剧/父母本人：5% + 反常识/创新形式：5%（探索）

单变量原则：一条素材只测一个变量（钩子/演员/场景/进入时间/主卖点/CTA）。`;
          }
        },
        {
          keywords: ['减少', '弱'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期投放数据（金额与数量已脱敏），建议减少或停止的方向：

【数据依据】
1. 达人二剪（非明星）：消耗占 3.0%，转化成本 1783.24，比整体 1598.17 高 11.6%
2. 恐吓/坑点科普向（如「只要中了这3个坑 小心一分不赔」）：转化成本 3011.19，约为整体 2 倍，点击率 3.36% 但转化率仅 0.61%
3. 搬运打压素材：平台判定搬运，当期消耗仅剩 145.72 元

【判断】
1. 普通达人二剪既没有明星素材的转化效率，也没有原生标题的规模效应，处于「夹在中间」的尴尬位置
2. 恐吓素材的典型病症：前3秒吓住人了（点击高），但用户防御心理被激起，后端不转化——这也符合「不用恐吓、羞辱或道德绑架推动购买」的合规原则

【下一轮建议】
1. 非明星达人二剪暂停，授权预算集中给明星二剪
2. 坑点/恐吓类选题全部停投，改成「反常识+快速反转」结构（先别急着买 → 3秒内反转成先看能不能投）
3. 搬运素材立即下线，避免账户权重受损`;
          }
        }
      ],
      fallbackResponse: '我理解你的问题。优化助手当前预置了投放数据相关的常见问题，你可以试试：最近什么素材表现最好、哪类素材跑量能力最强、首发率/优质率对比、真人和AI素材差异、下一轮重点方向等。'
    },
    planning: {
      quickPrompts: [
        '给我素材方向',
        '给我选题',
        '给我设计演员和人物关系',
        '给我场景方向',
        '给我写实拍情景剧',
        '给我写口播',
        '给我写AI剧情',
        '帮我写完整脚本',
        '帮我审脚本'
      ],
      mockResponses: [
        {
          keywords: ['素材方向', '方向'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解（36条头部素材沉淀），给你3个完整方向建议：

━━━━━━━━━━━━━━━━━━━━
## 方向一：慢病/结节可投清单型（S1 · 历史成熟度最高）
- **目标人群**：父母有三高/结节/糖尿病的25-45岁子女
- **人物**：女儿或儿子（25-35岁），朴素居家穿着
- **人物关系**：第一人称子女讲述
- **场景**：疾病清单图前贴 + 居家/医院真人承接
- **需求触发**：父母体检报告出来，医生提醒慢病管理
- **痛点**：爸妈身体有小毛病，很多重疾险买不了
- **前三秒Hook**：「爸妈有高血压、糖尿病、结节，重疾险是不是买不了了？」
- **核心冲突**：想给父母买 vs 以为买不了
- **剧情结构**：清单筛选 → 「别急着放弃」→ 限定投保条件 → 产品责任 → 点击测算
- **产品承接**：三大结节可投；高血压、糖尿病无并发症且满足健告其他要求可投；责任内最高100万
- **视觉/表演风格**：清单图首帧加条件小字，真人语速平缓像朋友聊天
- **为什么值得做**：当期原生标题类占消耗52.2%、成本1516.34；36条素材确认这是最强高意向筛选器

━━━━━━━━━━━━━━━━━━━━
## 方向二：医院/医保缺口型（A1 · 最强信任）
- **目标人群**：刚陪父母看病/体检/住院的子女
- **人物**：30-40岁子女，医院环境自拍
- **人物关系**：第一人称陪护经历分享
- **场景**：医院结算自助机旁/住院部走廊外（不拍患者隐私）
- **需求触发**：刚看到医保报销后的自费结算单
- **痛点**：有医保≠够用，大病现金流压力
- **前三秒Hook**：「陪爸妈住一次院才知道，有医保也不是万事大吉。」
- **核心冲突**：以为有医保就够 vs 结算单面前才发现缺口
- **剧情结构**：事件切入 → 医保边界 → 家庭现金流 → 产品责任（住院治疗10万/先进治疗50万/康复护理40万）→ CTA
- **产品承接**：责任内最高100万，按不同责任给付，具体以条款为准
- **视觉/表演风格**：手持自拍真实感，字幕卡呈现责任数字
- **为什么值得做**：医院是S级信任场景；成本最优素材「低谷陪护」1239.97就是这类情绪结构

━━━━━━━━━━━━━━━━━━━━
## 方向三：家庭冲突短剧型（B2 · 情绪组件）
- **目标人群**：父母不愿买保险的普通家庭
- **人物**：女儿（28-35岁）+ 妈妈（55-65岁）
- **人物关系**：母女，妈妈是「反对者」
- **场景**：家庭客厅/餐桌
- **需求触发**：家庭饭桌上父母说「不用买，浪费钱」
- **痛点**：父母觉得没必要，真出事还是孩子扛
- **前三秒Hook**：妈妈：「不用买，浪费钱。」女儿：「我不是怕你现在生病，我怕真要用钱的时候我们没准备。」
- **核心冲突**：父母拒绝 vs 子女的提前准备
- **剧情结构**：父母反对 → 女儿解释 → 产品责任 → 家庭收口
- **产品承接**：每月9.65元起，以页面测算为准；老人讲情绪，责任用字幕卡
- **视觉/表演风格**：生活化暖色调，不刻意煽情，一条只保留一个冲突
- **为什么值得做**：家庭关系解释了「为什么是我来买」；下沉市场和女性子女人群的双重覆盖

需要哪个方向的完整脚本，直接说「写实拍情景剧」「写口播」或「帮我写完整脚本」。`;
          }
        },
        {
          keywords: ['选题'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，当前值得测试的选题清单（按钩子机制分类）：

## 健康状态钩子（转化意图最强）
1. 爸妈有高血压，还能不能买重疾险？
2. 体检有结节，先别急着放弃重疾险。
3. 父母有糖尿病，是不是已经买不了了？

## 年龄窗口钩子（制造「现在了解」的理由）
4. 80/90后，爸妈没超过70岁的先看一下。
5. 父母快到60/65岁了，先别只看价格。

## 事件型钩子（信任和留存强）
6. 陪爸妈住一次院，我才发现有医保也不是万事大吉。
7. 爸爸急诊那天，我才真正明白保险为什么要提前看。

## 子女关系钩子（适合冷流量）
8. 小时候爸妈撑起我们，现在轮到我们提前准备。
9. 不能天天陪爸妈，至少把大病保障先看懂。

## 产品认知钩子（中高意向）
10. 这个100万，不是一个简单确诊金。
11. 给爸妈买重疾险，第一步不是看价格，是看健康告知。

## 人群清单钩子（第三轮新机制，重点测试）
12. 以下三类人，建议先帮父母了解：异地独生子女、父母有三高结节、担心长期护理。

## 反常识钩子（3-5秒内必须反转）
13. 先别急着给爸妈买重疾险。

测试原则：一次只测一个变量。想展开哪一条，直接说「展开第N个」。`;
          }
        },
        {
          keywords: ['演员', '人物关系'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，AI策略默认优先的人物组合设计：

## 1. 女儿 + 妈妈（最成熟组合）
- **演员形象**：女儿25-35岁职场/居家装扮；妈妈55-65岁普通居家
- **人物关系**：母女，妈妈是反对者（觉得贵、不想体检）
- **适合任务**：慢病问答、陪诊叙事、父母不愿买
- **注意**：老人只讲情绪和生活，复杂条款用字幕卡，不让老人背数字

## 2. 普通儿子 + 父母
- **演员形象**：朴素男性子女，不精致
- **人物关系**：父子/母子，家庭现金流承担者
- **适合任务**：大病费用、「兜底」叙事、男性付款人人群
- **注意**：不要太硬讲产品，会像销售

## 3. 医院普通人 + 子女视角
- **适合任务**：医保缺口、结算单冲击
- **注意**：不拍患者隐私、不虚构病例、不假装医护

## 4. 主播/专业讲解者 + 产品责任卡
- **适合任务**：100万责任拆分、健康告知解释
- **注意**：专业身份要真实，不能冒充医生；控制占比避免同质化

## 5. 老人本人 + 字幕产品卡
- **演员形象**：60-70岁普通老人，真实生活场景
- **适合任务**：「我以为买不上了」「不想拖累孩子」
- **为什么有效**：老人讲心态比子女替老人说更真实，下沉适配强

## 6. 异地子女单人自拍
- **场景**：高铁站/车内/机场
- **适合任务**：情绪冷开，「不能陪伴但想提前准备」

单变量测试建议：同一脚本在「25岁女儿 / 35岁成熟女性 / 30岁儿子 / 55岁妈妈本人 / 中年讲解者」之间轮换，观察点击率与表单率变化。`;
          }
        },
        {
          keywords: ['场景'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，场景库按优先级整理：

## S级场景（优先排产）
### 医院门诊/急诊/住院/结算
- **为什么有效**：医院是「风险已经发生」的证据环境，天然回答「这不是空谈疾病」
- **适合拍**：陪父母体检后、住院部出来、结算自助机旁、急诊门口、病房外走廊
- **不适合**：偷拍患者隐私、虚构病例检查单、假装医护、在医院喊「都赔」

### 疾病/慢病清单首帧
- **为什么有效**：不是场景，是意图筛选器——「我爸刚好高血压，我要不要继续看？」
- **注意**：只能用确认口径（三大结节；高血压/糖尿病无并发症），不能自行扩充病种清单

## A级场景
### 家庭客厅/餐桌/厨房
- **核心价值**：呈现「谁在替谁做决定」；最好冲突：父母「不用买浪费钱」vs 子女「怕真要用钱时没准备」

### 高铁站/机场/车内
- **核心价值**：异地子女的视觉符号，「我在外地工作，不能一直陪父母」

### 普通父母真实生活（餐馆/雪地/小区/公园）
- **核心价值**：降低商业片感，适合下沉市场和老人本人出镜

## B级场景：棚拍主播/专家
- 快速下结论、提高信息密度、标准化生产；但同质化严重，只做「前贴组件」

## C级场景（创新池）：长剧情/纪录片式病房内容
- 高沉浸强情绪，但版权/肖像/真实性风险最高，产品进入晚；建议不超过总产能10%-15%`;
          }
        },
        {
          keywords: ['情景剧'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，这是一个实拍情景剧完整方案：

# 家庭短剧：妈，这不是浪费钱

## 人物
- **女儿**：30岁，城市上班族，居家便装
- **妈妈**：58岁，退休，围裙居家造型

## 场景
家里厨房+餐桌，周末中午，妈妈在择菜。

## 完整脚本

**(0-5s Hook)**
妈妈（头也不抬）：别瞎花钱啊，我身体好着呢，买什么保险。
女儿：妈，我不是怕你现在生病。

**(6-15s 冲突升级)**
女儿（把手机推过去）：我是怕真要用钱的时候，咱们家没准备。你看王阿姨家，去年住院自费那一块，最后还不是孩子凑的。
妈妈（停下手）：那能一样吗，我这把年纪……

**(16-30s 产品进入)**
女儿：我就是先看了看，这款最高70岁符合条件能投，你有高血压但没并发症，也符合健康告知的话有机会投。真确诊了大病，它不是只赔一次：住院治疗、先进治疗、康复护理是分开的责任，责任内最高100万。

**(31-40s 条件+收口)**
妈妈：多少钱？
女儿：每月9.65元起，具体多少要按你的年龄在页面测算，90天等待期这些条款我都看过了。你要不也看看？
（字幕卡：投保需符合健康告知条件，具体责任以保险条款为准）

## 情绪与风格
- 暖色调、生活化，不刻意煽情，一条只保留「父母反对 vs 子女准备」一个冲突
- 妈妈讲情绪，女儿讲责任，数字全部走字幕卡

## 合规检查
✅ 慢病可投已绑定健康告知条件
✅ 100万按责任拆分表达，未说确诊赔100万
✅ 价格带「起」和「以页面测算为准」
✅ 提到90天等待期
✅ 无绝对化用语`;
          }
        },
        {
          keywords: ['口播'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，这是一个实拍口播完整脚本（慢病可投清单型）：

# 口播：爸妈有慢病，先别急着放弃

## 基本信息
- **人物**：30岁女儿，居家客厅自拍
- **形式**：疾病清单图前贴 + 真人口播
- **时长**：40秒（冷信息流节奏）

## 脚本

**(0-3s Hook)**
（首帧：疾病清单图——高血压/糖尿病/三大结节，配条件小字）
> 爸妈有高血压、糖尿病、结节，重疾险是不是买不了了？先别急着放弃。

**(4-10s 场景痛点)**
> 上个月陪我妈体检，报告出来那一刻我第一反应就是：完了，是不是什么保险都买不了了。后来我才知道，不是。

**(11-20s 产品进入+可投条件)**
> 像这款保险项目，三大结节有机会投；高血压、糖尿病没有并发症，满足健康告知其他要求，也有机会投。18到70岁，符合投保条件就行。

**(21-32s 责任拆分)**
> 关键是这100万不是一个确诊金：住院治疗10万，先进治疗50万，康复护理40万，按不同责任给付。住院治疗满足条件，每个月还能赔5000，最多10个月。125种重大疾病，具体病种看条款。

**(33-40s 条件+CTA)**
> 每月9.65元起，具体多少，按年龄和健康情况在页面测算为准。90天等待期，意外无等待期。给爸妈的保障，先点下方测一测能不能投。

## 拍摄建议
- 清单图首帧只放确认口径病种，条件小字必须清晰
- 语速中等，朋友聊天感；责任数字用字幕卡同步
- 产品进入约11秒，符合冷信息流节奏（0-3筛人 → 3-10痛点 → 8-15进入 → 15-35责任）

## 合规检查
✅ 慢病可投绑定健告条件，无「都能买」
✅ 100万按责任拆分
✅ 价格带「起」和「以页面测算为准」
✅ 90天等待期已说明
✅ 125种重疾注明详见条款`;
          }
        },
        {
          keywords: ['AI剧情'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，这是一个AI剧情方向方案（长剧情创新池）：

# AI剧情：爸爸出院之后

## 定位
属于形式H「长剧情/纪录片式」创新池——历史素材中约3分钟的长剧情是差异最大的样本，适合内容型流量测试。

## 基本信息
- **人物**：儿子（32岁）、父亲（65岁，脑梗康复期）
- **人物关系**：父子，父亲从「家里的顶梁柱」变成「被照顾的人」
- **场景**：病房 → 出院 → 家中康复日常
- **视觉风格**：纪实感、自然光、少配乐

## 分镜结构（约90秒版）
**(0-10s)** 医院走廊，儿子拿着结算单发呆。旁白：「爸爸脑梗那天，我才明白什么叫『一场病改变一个家』。」
**(11-30s)** 治疗过程闪回：急诊、病房、家人轮流陪护。情绪点：父亲说「别耽误工作，我自己行」。
**(31-55s)** 出院后：康复训练、母亲喂药、儿子深夜查「康复护理费用」。产品责任卡出现：康复护理保险金40万，达到条款约定状态要求可申请给付。
**(56-75s)** 儿子旁白：「提前给爸妈准备保障，不是怕他们生病，是怕生病的时候，家里人只能硬扛。」责任卡：住院治疗10万/先进治疗50万/康复护理40万，责任内最高100万。
**(76-90s)** 条件字幕+CTA：投保需符合健康告知，90天等待期，具体以条款和页面测算为准。点击测算。

## 使用边界（重要）
1. 产能占比不超过10%-15%，只在你明确要「剧情/情绪/长视频」时使用
2. 不得使用影视素材、真实病例、虚构理赔单；医疗画面用自拍情景还原
3. 产品进入偏晚（约30秒后），接受「高完播、可能低点击」的测试定位
4. AI生成画面需人工修片：手指、病历文字、人脸一致性必须逐帧检查`;
          }
        },
        {
          keywords: ['写脚本', '脚本'],
          response: (project, _conversation) => {
            return `需要什么类型的脚本？基于「${project.name}」AI拆解，可以选择：

1. 实拍口播（慢病可投清单型 / 医院结算型 / 异地子女型）
2. 实拍情景剧（家庭餐桌冲突 / 父母不愿买）
3. AI剧情（长剧情创新池）
4. 评论问答型（单病种单问题，适合搜索流量）

请告诉我你的需求，也可以直接说「写实拍情景剧」「写口播」。`;
          }
        },
        {
          keywords: ['审脚本', '审核', '评分'],
          response: (project, _conversation) => {
            return `## 脚本审核评分（10分制）：**总分 6.2 / 10**

| 维度 | 得分 | 评语 |
|------|------|------|
| 前3秒钩子 | 0.8/1.5 | 「给大家讲讲保险」开头没有点名父母/慢病/医院，筛选力弱 |
| 人群精准度 | 0.7/1.0 | 观看者不明确：对子女说还是对父母说，角色混乱 |
| 场景与痛点 | 1.0/1.5 | 有医院背景，但没有具体事件（结算单/体检报告） |
| 叙事与留存 | 0.8/1.0 | 中段在堆卖点，没有推进 |
| 产品承接 | 1.0/1.5 | 产品出现太晚（约25秒），且只说「最高100万」没拆责任 |
| 信任机制 | 0.7/1.0 | 缺少第二种信任来源（清单/产品页/普通人） |
| 行动引导 | 0.4/0.5 | CTA模糊（「可以考虑一下」） |
| 合规与准确性 | 0.8/2.0 | 出现「覆盖100多种大病」接近绝对化表达，需改为「保障125种重大疾病，具体详见条款」 |

**评级：6.0-6.9 = 问题较多，需要重写核心结构，不建议直接上线。**

## 最大问题
1. 前3秒没有完成「筛人」——用户不知道这条视频和自己有什么关系
2. 100万没有拆责任，前端承诺宽、后端容易落差（点击高转化低的典型病因）

## 修改建议
1. 开头改为具体钩子：「爸妈有高血压，还能不能买重疾险？」（点名人群+慢病）
2. 产品提前到8-15秒进入，并立即拆责任：住院治疗10万/先进治疗50万/康复护理40万
3. 补条件表达：每月9.65元起以页面测算为准；慢病可投绑定健告条件；90天等待期
4. CTA明确：「先点下方测一测能不能投」

## 合规封顶提醒
出现「都能买/100%覆盖/确诊就赔100万/买了就赔」任一表达，脚本最高只能评 6.9 分并强制退回。`;
          }
        },
        {
          keywords: ['第二个', '展开'],
          response: (project, conversation) => {
            const item = extractNthItem(conversation, 2);
            if (!item) {
              return '你想要展开哪一个？请告诉我序号或者名称。';
            }
            return `「${item.trim().slice(0, 30)}…」详细展开（完整执行方案）：

## 前3秒钩子的三个候选（单变量测试用）
1. 健康状态钩子：「爸妈有高血压、糖尿病、结节，重疾险是不是买不了了？」
2. 年龄窗口钩子：「80/90后，爸妈没超过70岁的先看一下。」
3. 事件型钩子：「陪爸妈住一次院才知道，有医保也不是万事大吉。」

## 完整口播框架（40秒冷信息流节奏）
**(0-3s)** 钩子首帧：具体人群+具体问题（清单图/评论截图/医院自拍三选一）
**(4-10s)** 场景与痛点：体检报告/结算单/车站送别，一个具体事件
**(11-20s)** 产品进入：18-70岁符合条件可投；三大结节可投，高血压糖尿病无并发症满足健告可投
**(21-32s)** 责任拆分：住院治疗10万/先进治疗50万/康复护理40万，责任内最高100万按责任给付
**(33-40s)** 条件+CTA：每月9.65元起以页面测算为准；90天等待期；「先点下方测一测能不能投」

## 单变量测试建议
- 固定：后段责任拆分和CTA不动
- 只换：前3秒钩子（上面三个候选轮换）
- 观察：3秒率、5秒率、点击率

## 合规底线（每版都要检查）
无「都能买/100%覆盖/确诊就赔」；价格带「起」和「以页面测算为准」；病种举例注明详见条款。`;
          }
        },
        {
          keywords: ['展开', '详细', '介绍'],
          response: (project, conversation) => {
            const item = extractNthItem(conversation, 1);
            if (!item) {
              return '你想要展开哪一个？请告诉我序号。';
            }
            return `${item.trim()}——展开说明：

我基于「${project.name}」AI拆解 给你这个方向的执行框架：

### 目标人群分析
这个方向主要面向${project.productUnderstanding.targetAudience}。他们最核心的卡点是：${project.productSkill.needsAndPainPoints[0].painPoint}。所以内容必须先回答「能不能买」，再讲「保多少」。

### 内容创作要点
1. **Hook**：前3秒点名具体人群（父母/慢病/年龄窗口），越具体越降低无关感
2. **承接**：8-15秒产品进入，先说可投条件，再说责任
3. **责任**：100万拆成住院治疗10万/先进治疗50万/康复护理40万，不做单一确诊金表达
4. **信任**：至少两种信任组合（清单+真人 / 医院+子女视角 / 家庭情绪+责任卡）
5. **CTA**：结尾明确「点击测算」，不模糊

### 合规条件
每月9.65元起以页面测算为准；慢病可投绑定健告条件；125种重疾详见条款；90天等待期。

需要我按这个框架写完整脚本吗？`;
          }
        },
        {
          keywords: ['15秒', '压缩', '短版本', '精简'],
          response: (project, _conversation) => {
            return `压缩到15秒版本（高意向流量适用）：

**(0-3s)**
> 爸妈有高血压，还能买重疾险吗？

**(4-8s)**
> 三大结节有机会投；高血压、糖尿病没并发症，满足健告也有机会。最高70岁符合条件可投。

**(9-12s)**
> 责任内最高100万，住院治疗、先进治疗、康复护理分开保。

**(13-15s)**
> 每月9.65元起，以页面测算为准。点下方，先测能不能投。`;
          }
        }
      ],
      fallbackResponse: '我理解你的问题。创意助手当前预置了素材方向、选题、演员人物关系、场景、脚本类问题的回答，你可以试试快捷问题，或直接说「给我素材方向」「帮我写口播」。'
    },
    editing: {
      quickPrompts: [
        '帮我拆素材结构',
        '这条素材哪些部分值得保留？',
        '二剪应该怎么做？',
        '前3秒应该怎么换？',
        '高点击素材怎么继续剪？',
        '高转化素材怎么继续剪？',
        '给我一个拼接方案',
        '帮我优化素材节奏'
      ],
      mockResponses: [
        {
          keywords: ['拆', '结构'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，用当前最典型的跑量结构给你拆（慢病可投清单型，40秒）：

【原素材结构】
- **0-3s**：疾病清单图首帧（高血压/糖尿病/三大结节 + 条件小字）——意图筛选器
- **3-8s**：痛点提问「是不是买不了了？先别急着放弃」
- **8-15s**：产品进入：18-70岁符合条件可投，慢病可投条件说明
- **15-30s**：责任拆分段：住院治疗10万 / 先进治疗50万 / 康复护理40万，字幕卡同步
- **30-40s**：信任+CTA：每月9.65元起以页面测算为准，90天等待期，点击测算

【结构判断】
这是36条头部素材验证过的标准链路：清单筛人 → 回答「能不能买」→ 责任拆分降落差 → 条件收口。

【建议保留】
1. 清单首帧（筛选效率最高的组件，但条件小字必须清晰）
2. 责任拆分段（减少前端承诺与后端认知的落差）
3. 条件字幕（合规刚需，也是信任组件）

【建议替换】
中段痛点讲述若与首帧病种不一致（清单讲结节、口播讲医保），会产生拼接感——前后逻辑必须对齐。

需要的话我可以继续给你「二剪方案」「拼接方案」或「节奏建议」。`;
          }
        },
        {
          keywords: ['保留'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期数据，一条素材里最值得保留的部分：

【结论】
高表现素材的共性模块是：清单/情感首帧、责任拆分段、条件字幕。剪辑时优先保留这三个模块。

【数据依据】
- 原生标题情感向成本最优三条（999~1372元）的共性：真实家庭/陪护情绪开场 + 完整责任拆分
- 直出口播点击率 5.43% 全量最高，它的钩子段（责任讲解直给）值得抽出来复用
- 明星二剪转化率 1.91%，后段产品承接结构是效率来源

【建议保留清单】
1. **首帧筛选模块**：疾病清单图/评论问题截图（前3秒决定3秒率）
2. **责任卡模块**：10万+50万+40万的字幕卡段落（所有素材统一，建立产品认知一致性）
3. **条件字幕模块**：健告条件/价格以测算为准/90天等待期（合规+信任双重作用）
4. **真人情绪段落**：父母/子女的真实反应镜头（下沉市场特别吃这个）

【建议删除】
与主题无关的空镜、重复的产品名口播（同一条内说3遍以上会引起反感）、任何绝对化表述画面。`;
          }
        },
        {
          keywords: ['二剪'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期数据，二剪应该这样做：

【结论】
当前成本最优的形式就是二剪（明星二剪 CPA 999.61），二剪的正确姿势是「拆模块 + 单变量」，不是重新剪一条。

【数据依据】
- 明星二剪：转化成本 999.61，转化率 1.91%，形式整体占消耗 44.8%
- 普通达人二剪：CPA 1783.24（差11.6%）——差距不在剪法，在授权素材本身的质量

【二剪方案】
1. **拆胜出模块**：把原片拆成 首帧 / 第一句话 / 情绪段 / 责任卡 / CTA 五个模块，标注哪段是「胜出模块」
2. **只换一个变量**：新版本保留1-2个胜出模块，其余不变。例如：保留责任卡+CTA，只换前3秒钩子
3. **重投前检查**：授权有效期是否覆盖新投放周期；条件字幕（健告/价格/等待期）是否还在；动态信息是否过期
4. **命名规范**：素材名按「方向-人群-主题-形式-版本」记录，方便回收对比

【提醒】
未授权或包含个人敏感信息的素材禁止进入回收库；明星授权到期前要提前规划续期或替换。`;
          }
        },
        {
          keywords: ['前3秒', '开头'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，前3秒替换方案（按钩子机制给三套）：

【方案一：健康状态钩子】（转化意图最强）
> 画面：疾病清单图 + 条件小字
> 「爸妈有高血压、糖尿病、结节，重疾险是不是买不了了？」
> 适合：高意向慢病家庭，转化导向

【方案二：年龄窗口钩子】（规模导向）
> 画面：子女对镜直讲
> 「80/90后，爸妈没超过70岁的，先看一下。」
> 适合：冷流量筛选「父母年纪+子女身份」双标签

【方案三：事件型钩子】（信任导向）
> 画面：医院结算自助机/住院部外
> 「陪爸妈住一次院才知道，有医保也不是万事大吉。」
> 适合：建立信任，配合后段责任拆分

【替换规则】
1. 一次只换前3秒，后段全部不动（单变量原则）
2. 钩子必须和后段承接逻辑一致：清单钩子接慢病可投，医院钩子接医保缺口
3. 反常识钩子（「先别急着给爸妈买」）必须在3-5秒内反转，否则就是标题党
4. 每个版本独立命名记录，观察3秒率和5秒率的差异`;
          }
        },
        {
          keywords: ['高点击'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期数据，高点击素材的继续剪法：

【结论】
高点击低转化的素材（如直出口播：点击率 5.43%，转化成本 1316.16）问题不在开头，在「前端承诺与后端承接的落差」。

【数据依据】
- 直出口播：点击率 5.43% 全量最高，转化率 2.19% 中上——它其实是健康的，但要防止放量后转化稀释
- 反例：「只要中了这3个坑」点击率 3.36% 但转化率 0.61%，转化成本 3011.19——典型的前端钩子过宽

【诊断逻辑】（AI拆解 复盘规则）
点击高、转化低 = 前端承诺过宽，或健康告知/价格/责任表达与落地页不一致。

【继续剪的动作】
1. **检查承诺宽度**：开头说的和落地页给的是不是一回事？「能买」必须绑定健告条件
2. **提前责任卡**：把 10万+50万+40万 责任拆分段从 15-30s 提前到 10-25s，让高意向用户更快看到「到底保什么」
3. **强化CTA**：结尾改为明确动作「先点下方测一测能不能投」，去掉模糊引导
4. **保留钩子**：点击率高说明筛选有效，钩子段不动`;
          }
        },
        {
          keywords: ['高转化'],
          response: (project, _conversation) => {
            return `基于「${project.name}」当期数据，高转化素材的继续剪法：

【结论】
高转化素材（明星二剪：转化率 1.91%、成本 999.61；低谷陪护：成本 1239.97）的剪法是「保后段、换前段」——它的后段转化结构已验证，缺的是消耗规模。

【数据依据】
- 明星二剪转化成本 999.61 是当期最优，但单条消耗只有 0.8万，属于「高转化低消耗」
- AI拆解 复盘规则：高转化但低消耗 = 可保留后段转化结构，重新测试前3秒和首帧

【继续剪的动作】
1. **锁定后段**：产品承接、责任卡、条件字幕、CTA 全部不动——这是转化效率的来源
2. **裂变前段**：换3版前3秒（健康状态钩子/年龄窗口钩子/事件型钩子），其余帧一致
3. **换首帧不换逻辑**：清单图、评论截图、真人直讲三种首帧都试，但必须与后段承接逻辑对齐
4. **放量观察**：每个版本给到足够消耗（≥5000）再判断，避免看几小时的早期数据就下结论

【注意】
裂变版本沿用同一授权素材时，确认授权范围覆盖衍生剪辑。`;
          }
        },
        {
          keywords: ['拼接'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，给你一个拼接方案：

【推荐拼接：清单前贴 + 直出讲解 + 情感收口】
- **0-3s**：疾病清单图首贴（A素材的筛选模块）——高血压/糖尿病/三大结节+条件小字
- **3-15s**：直出口播中段（B素材的讲解模块）——「别急着放弃」+ 可投条件说明
- **15-28s**：责任卡段（统一组件）——住院治疗10万/先进治疗50万/康复护理40万
- **28-38s**：家庭情感收口（C素材的情绪模块）——「小时候爸妈撑起我们」+ 真实家庭镜头
- **38-42s**：条件+CTA——每月9.65元起以页面测算为准，点击测算

【拼接三原则】
1. **逻辑一致**：前贴讲慢病可投，后贴就接可投条件——前后贴逻辑不一致会产生明显拼接感（历史素材里主播冷开+达人后贴最常见的失败原因）
2. **声音统一**：拼接段的语速、音量、背景音乐要重新对齐，必要时整体重配旁白
3. **授权可追溯**：每一段素材的来源和授权状态必须可追溯，动态信息（价格/责任数字）以最新版为准

【适用场景】
适合批量产能：模块库建好后，同一责任卡段可以配 3 种前贴 × 2 种收口，快速裂变。`;
          }
        },
        {
          keywords: ['节奏'],
          response: (project, _conversation) => {
            return `基于「${project.name}」AI拆解，素材节奏优化建议：

【三套默认节奏模板】
1. **冷信息流**（默认）：0-3s 具体人群/事件/问题 → 3-10s 生活场景与痛点 → 8-15s 产品进入 → 15-35s 只讲1-2个最相关责任 → 35s后 条件+CTA
2. **搜索DSP/高意向**：0-3s 产品名/问题直接前置 → 3-10s 能不能投 → 10-25s 责任拆分 → 25s后 条件+CTA
3. **情绪故事**：0-8s 事件/人物 → 8-20s 冲突升级 → 15-30s 产品进入 → 30s后 责任与行动

【节奏红线】
1. 产品超过30秒仍未出现：这条内容的目标是「完播」而不是「转化」，要确认这是不是你想要的
2. 15-35秒只讲1-2个责任，不要把10万/50万/40万/125种/9.65元全塞进去——堆卖点会让中段流失

【按数据调节奏】
- 3秒低 → 首帧和第一句重做（钩子不够具体）
- 3秒高、后段掉 → 第5-15秒加真实场景或产品承接
- 完播高、点击低 → 产品进入太晚，提前到15-30s并加强CTA`;
          }
        }
      ],
      fallbackResponse: '我理解你的问题。后期助手当前预置了素材结构、二剪、拼接、节奏类问题的回答，你可以试试快捷问题，或直接说「帮我拆素材结构」「二剪应该怎么做」。'
    }
  }
};
